import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { GroupsListPane } from '@/components/settings/team/groups-list-pane';
import { GroupEditorPane } from '@/components/settings/team/group-editor-pane';
import { createPermissionGroup, deletePermissionGroup, fetchPermissionGroups } from '@/lib/api/team';

/**
 * Settings → Team → Access groups: the list ↔ single-group editor flow (mirrors the queues
 * sub-page). The editor saves one group at a time with an explicit PUT and patches the list
 * on success; New group creates an empty group server-side and jumps straight into it.
 */
export function TeamGroupsSubPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState({ mode: 'list' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPermissionGroups()
      .then((res) => {
        if (cancelled) return;
        setGroups(res.items);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoading(false);
        toast.error(error instanceof Error ? error.message : 'Could not load access groups');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await createPermissionGroup({ name: 'New group', description: '', permissions: {} });
      setGroups((prev) => [...prev, created]);
      setView({ mode: 'edit', group: created });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create access group');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (id) => {
    const group = groups.find((g) => g.id === id);
    if (group) setView({ mode: 'edit', group });
  };

  const handleDelete = async (group) => {
    const ok = window.confirm(
      `Delete “${group.name || 'this group'}”? Employees referencing it simply lose its rights.`,
    );
    if (!ok) return;
    try {
      await deletePermissionGroup({ groupId: group.id });
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      setView({ mode: 'list' });
      toast.success('Access group deleted.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete access group');
    }
  };

  const handleSaved = (updated) => {
    setGroups((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
  };

  if (view.mode === 'edit') {
    return (
      <GroupEditorPane
        key={view.group.id}
        initialGroup={view.group}
        groups={groups}
        onBack={() => setView({ mode: 'list' })}
        onSaved={handleSaved}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <GroupsListPane
      groups={groups}
      loading={loading}
      creating={creating}
      onCreate={handleCreate}
      onEdit={handleEdit}
    />
  );
}
